/**
 * The ONE place that registers hub/public/firebase-messaging-sw.js.
 *
 * Its own module (PR #1242 refute round) so the app shell can register the
 * worker without loading lib/push.ts, which pulls firebase/messaging. When
 * appShell.ts reached this through a dynamic import of push.ts, a push chunk
 * that failed to load also skipped the white-screen worker for that visit.
 * Nothing here imports anything, so it costs the first load next to nothing.
 * Keep it that way: no firebase/messaging, directly or indirectly.
 */

/**
 * Register the messaging service worker. The Firebase web config rides in
 * the query string so the public worker file never hardcodes keys.
 *
 * SHARED ON PURPOSE (THE WHITE SCREEN, piece 2, 2026-08-22) because this is
 * now also the app-shell caching worker (see hub/public/firebase-messaging-sw.js),
 * and src/lib/appShell.ts calls this SAME function, with this SAME URL
 * shape, unconditionally on every load - not just when someone opts into
 * push (src/lib/push.ts calls it too, before minting a token). That "same
 * function, same URL" part is load-bearing, not incidental: a service worker
 * registration is keyed by SCOPE, and the Service Worker spec skips
 * reinstalling when the newly fetched script's BYTES match what is already
 * active, regardless of the request URL used to fetch it. If the app-shell
 * caller ever registered `/firebase-messaging-sw.js` bare (no query string)
 * while push.ts keeps registering it with the config query string, whichever
 * call happened to install the worker FIRST on a given device would win
 * permanently (until the file's content next changes on a deploy) - and if
 * that was the bare, query-less URL, every `self.location.search` read inside
 * the worker comes back empty and firebase.initializeApp() gets nulls, which
 * is a silent, total, per-device push outage of exactly the kind
 * #firebase-messaging-sw.js's own history warns about. Sharing this one
 * function is what makes calling it from two places safe. Do not give
 * appShell.ts or push.ts their own copy of the URL-building logic.
 */
export async function registerMessagingWorker(): Promise<ServiceWorkerRegistration> {
  const cfg = new URLSearchParams({
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
  });
  return navigator.serviceWorker.register(`/firebase-messaging-sw.js?${cfg.toString()}`);
}
